import { useEffect, useState } from 'react';
import { Command } from 'cmdk';
import * as Dialog from '@radix-ui/react-dialog';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CornerDownLeft, Moon, Search, User } from 'lucide-react';
import { ALL_NAV } from '../../layout/nav.js';
import { api } from '../../api/instance.js';
import { Kbd } from './misc.js';
import { toggleTheme } from '../../lib/theme.js';

/** Cmd/Ctrl+K: jump to any page or person. */
export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); onOpenChange(!open); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);
  useEffect(() => { if (!open) setQ(''); }, [open]);

  const term = q.trim();
  const people = useQuery({
    queryKey: ['contacts', 'palette', term],
    enabled: open && term.length >= 2,
    queryFn: () => api.call('listContacts', { query: { q: term, limit: 8 } }),
  });

  const go = (path: string) => { onOpenChange(false); navigate(path); };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-slate-950/40 animate-fade-in" />
        <Dialog.Content className="fixed left-1/2 top-[12vh] z-50 w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 overflow-hidden rounded-xl border bg-popover text-popover-foreground shadow-pop animate-zoom-in">
          <Dialog.Title className="sr-only">Search</Dialog.Title>
          <Dialog.Description className="sr-only">Jump to a page or a person</Dialog.Description>
          <Command label="Search" shouldFilter={true}>
            <div className="flex items-center gap-2 border-b px-4">
              <Search className="h-4 w-4 text-muted-foreground" aria-hidden />
              <Command.Input value={q} onValueChange={setQ} placeholder="Search pages and people…"
                className="h-12 w-full bg-transparent text-[15px] outline-none placeholder:text-muted-foreground" />
              <Kbd>Esc</Kbd>
            </div>
            <Command.List className="max-h-[60vh] overflow-y-auto p-2">
              <Command.Empty className="px-3 py-6 text-center text-sm text-muted-foreground">
                {people.isFetching ? 'Searching…' : 'No results.'}
              </Command.Empty>
              {people.isError && <p className="px-3 py-2 text-sm text-danger">Couldn't search people. Try again.</p>}
              {(people.data?.items.length ?? 0) > 0 && (
                <Command.Group heading="People" className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground">
                  {people.data?.items.map((c) => (
                    <Command.Item key={c.id} value={`person ${c.name} ${c.id}`} onSelect={() => go(`/reach?person=${c.id}`)}
                      className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm aria-selected:bg-muted">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span className="truncate">{c.name}</span>
                      {c.designation && <span className="truncate text-xs text-muted-foreground">{c.designation}</span>}
                    </Command.Item>
                  ))}
                </Command.Group>
              )}
              <Command.Group heading="Pages" className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground">
                {ALL_NAV.map((n) => (
                  <Command.Item key={n.key} value={`${n.label} ${n.description}`} onSelect={() => go(n.path)}
                    className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm aria-selected:bg-muted">
                    <n.icon className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{n.label}</span>
                    <span className="hidden truncate text-xs text-muted-foreground sm:inline">{n.description}</span>
                    <CornerDownLeft className="ml-auto hidden h-3.5 w-3.5 text-muted-foreground [[aria-selected=true]_&]:block" />
                  </Command.Item>
                ))}
              </Command.Group>
              <Command.Group heading="Actions" className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground">
                <Command.Item value="switch dark light theme" onSelect={() => { toggleTheme(); onOpenChange(false); }}
                  className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm aria-selected:bg-muted">
                  <Moon className="h-4 w-4 text-muted-foreground" /> Switch light / dark
                </Command.Item>
              </Command.Group>
            </Command.List>
          </Command>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
